import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { auth, db } from "../api/firebase";
import { AppThemeContext } from "./AppShell";
import { countries } from "../constants/countries";

// 都道府県リスト
const prefectures = [
  "北海道",
  "青森県",
  "岩手県",
  "宮城県",
  "秋田県",
  "山形県",
  "福島県",
  "茨城県",
  "栃木県",
  "群馬県",
  "埼玉県",
  "千葉県",
  "東京都",
  "神奈川県",
  "新潟県",
  "富山県",
  "石川県",
  "福井県",
  "山梨県",
  "長野県",
  "岐阜県",
  "静岡県",
  "愛知県",
  "三重県",
  "滋賀県",
  "京都府",
  "大阪府",
  "兵庫県",
  "奈良県",
  "和歌山県",
  "鳥取県",
  "島根県",
  "岡山県",
  "広島県",
  "山口県",
  "徳島県",
  "香川県",
  "愛媛県",
  "高知県",
  "福岡県",
  "佐賀県",
  "長崎県",
  "熊本県",
  "大分県",
  "宮崎県",
  "鹿児島県",
  "沖縄県",
  "海外（アメリカ）",
  "海外（その他）",
];

const JAPAN_PREFECTURES = prefectures.filter(
  (p) => p !== "海外（アメリカ）" && p !== "海外（その他）"
);

const initialValues = {
  registrationName: "",
  registrationFurigana: "",
  registrationEmail: "",
  registrationPhone: "",
  prefecture: "",
  country: "",
  city: "",
  usState: "",
  lat: null,
  lng: null,
  locationKey: "",
};

const countryFilterOptions = createFilterOptions({
  matchFrom: "any",
  stringify: (option) => `${option.label} ${option.keywords}`,
});

// ISOコード(2文字)から国旗絵文字を生成
const getCountryFlag = (keywords) => {
  if (!keywords) return "";
  const match = keywords.match(/\b([A-Z]{2})\b/);
  if (!match) return "";
  const code = match[1];
  return String.fromCodePoint(
    ...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
  );
};

const cityFilterOptions = createFilterOptions({
  matchFrom: "any",
  stringify: (option) =>
    typeof option === "string" ? option : option?.name || "",
  trim: true,
});

export default function RequiredUserInfoDialog() {
  const contextTheme = useContext(AppThemeContext);

  const theme = contextTheme || {
    surfaceBg: "#FFFFFF",
    bottomBorder: "rgba(0,0,0,0.08)",
    railActive: "#5A35FF",
    railText: "rgba(60,40,120,0.85)",
  };

  const [values, setValues] = useState(initialValues);
  const [openDialog, setOpenDialog] = useState(false);
  const [openThankYouDialog, setOpenThankYouDialog] = useState(false);
  const [errors, setErrors] = useState({});

  const [citiesByPrefecture, setCitiesByPrefecture] = useState({});
  const [loadingCities, setLoadingCities] = useState(true);

  const [usStatesByName, setUsStatesByName] = useState({});
  const [loadingUsStates, setLoadingUsStates] = useState(true);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[0-9+\-() ]{7,20}$/;

  const isJapanesePrefecture = useMemo(() => {
    return JAPAN_PREFECTURES.includes(values.prefecture);
  }, [values.prefecture]);

  const isAmerica = values.prefecture === "海外（アメリカ）";

  const usStateOptions = useMemo(() => {
    return Object.values(usStatesByName).flat();
  }, [usStatesByName]);

  const selectedUsState = useMemo(() => {
    return usStateOptions.find((s) => s.locationKey === values.locationKey) || null;
  }, [usStateOptions, values.locationKey]);

  const cityOptions = useMemo(() => {
    if (!isJapanesePrefecture) return [];
    return citiesByPrefecture[values.prefecture] || [];
  }, [isJapanesePrefecture, values.prefecture, citiesByPrefecture]);

  const selectedCity = useMemo(() => {
    return (
      cityOptions.find((c) =>
        typeof c === "string" ? c === values.city : c.name === values.city
      ) || null
    );
  }, [cityOptions, values.city]);

  useEffect(() => {
    let cancelled = false;

    const loadCities = async () => {
      setLoadingCities(true);
      try {
        const res = await fetch("/data/japanCities.json", {
          cache: "force-cache",
        });

        if (!res.ok) {
          throw new Error(`failed to load japanCities.json: ${res.status}`);
        }

        const json = await res.json();

        if (!cancelled) {
          setCitiesByPrefecture(json || {});
        }
      } catch (err) {
        console.error("load japanCities.json error:", err);
        if (!cancelled) {
          setCitiesByPrefecture({});
        }
      } finally {
        if (!cancelled) {
          setLoadingCities(false);
        }
      }
    };

    loadCities();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadUsStates = async () => {
      setLoadingUsStates(true);
      try {
        const res = await fetch("/data/usStates.json", {
          cache: "force-cache",
        });
        if (!res.ok) {
          throw new Error(`failed to load usStates.json: ${res.status}`);
        }
        const json = await res.json();
        if (!cancelled) {
          setUsStatesByName(json || {});
        }
      } catch (err) {
        console.error("load usStates.json error:", err);
        if (!cancelled) {
          setUsStatesByName({});
        }
      } finally {
        if (!cancelled) {
          setLoadingUsStates(false);
        }
      }
    };

    loadUsStates();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!auth.currentUser) return;

    const userDocRef = doc(db, "users", auth.currentUser.uid);

    const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
      if (!docSnap.exists()) return;

      const data = docSnap.data() || {};

      const next = {
        registrationName: data.registrationName || "",
        registrationFurigana: data.registrationFurigana || "",
        registrationEmail: data.registrationEmail || "",
        registrationPhone: data.registrationPhone || "",
        prefecture: data.prefecture || "",
        country: data.country || "",
        city: data.city || "",
        usState: data.usState || "",
        lat: typeof data.lat === "number" ? data.lat : null,
        lng: typeof data.lng === "number" ? data.lng : null,
        locationKey: data.locationKey || "",
      };

      setValues(next);

      // ★ pending write がある間はサーバ側の完全な状態が確定していないため、
      //   フィールド欠落と誤判定してダイアログが一瞬開く（フラッシュ）。
      //   pending が解消されたスナップショットだけで openDialog を更新する。
      if (docSnap.metadata.hasPendingWrites) {
        return;
      }

      const missingBasic =
        !String(next.registrationName).trim() ||
        !String(next.registrationFurigana).trim() ||
        !String(next.registrationEmail).trim() ||
        !String(next.registrationPhone).trim() ||
        !String(next.prefecture).trim();

      const missingJapanCity =
        JAPAN_PREFECTURES.includes(next.prefecture) &&
        (!String(next.city).trim() ||
          !Number.isFinite(next.lat) ||
          !Number.isFinite(next.lng));

      const missingCountry =
        next.prefecture === "海外（その他）" && !String(next.country).trim();

      const missingUsState =
        next.prefecture === "海外（アメリカ）" && !String(next.usState).trim();

      const missing = missingBasic || missingJapanCity || missingCountry || missingUsState;

      setOpenDialog(Boolean(missing));
    });

    return () => unsubscribe();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setValues((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "prefecture") {
        next.city = "";
        next.usState = "";
        next.lat = null;
        next.lng = null;
        next.locationKey = "";

        if (value !== "海外（その他）") {
          next.country = "";
        }
      }

      return next;
    });

    setErrors((prev) => {
      const nextErrors = { ...prev, [name]: "" };

      if (name === "prefecture") {
        nextErrors.city = "";
        if (value !== "海外（その他）") {
          nextErrors.country = "";
        }
      }

      return nextErrors;
    });
  };

  const validate = () => {
    const newErr = {};

    if (!values.registrationName.trim()) {
      newErr.registrationName = "お名前を入力してください";
    }

    if (!values.registrationFurigana.trim()) {
      newErr.registrationFurigana = "ふりがなを入力してください";
    }

    if (
      !values.registrationEmail.trim() ||
      !emailRegex.test(values.registrationEmail)
    ) {
      newErr.registrationEmail = "正しいメールアドレスを入力してください";
    }

    if (
      !values.registrationPhone.trim() ||
      !phoneRegex.test(values.registrationPhone)
    ) {
      newErr.registrationPhone = "正しい電話番号を入力してください";
    }

    if (!values.prefecture) {
      newErr.prefecture = "居住地を選択してください";
    }

    if (JAPAN_PREFECTURES.includes(values.prefecture)) {
      if (!values.city.trim()) {
        newErr.city = "市区町村を選択してください";
      } else if (!Number.isFinite(values.lat) || !Number.isFinite(values.lng)) {
        newErr.city = "市区町村データが不正です。再選択してください";
      }
    }

    if (values.prefecture === "海外（その他）" && !values.country) {
      newErr.country = "国名を選択してください";
    }

    if (values.prefecture === "海外（アメリカ）" && !values.usState) {
      newErr.usState = "州を選択してください";
    }

    if (Object.keys(newErr).length > 0) {
      return { valid: false, errors: newErr };
    }

    return { valid: true, errors: {} };
  };

  const handleConfirm = async () => {
    const { valid, errors: vErrors } = validate();

    if (!valid) {
      setErrors(vErrors);
      return;
    }

    if (!auth.currentUser) return;

    const userDocRef = doc(db, "users", auth.currentUser.uid);

    const trimmedPrefecture = values.prefecture.trim();
    const trimmedCity = values.city.trim();
    const isJapan = JAPAN_PREFECTURES.includes(trimmedPrefecture);
    const isUS = trimmedPrefecture === "海外（アメリカ）";

    const isOther = trimmedPrefecture === "海外（その他）";

    const selectedState = isUS
      ? usStateOptions.find((s) => s.locationKey === values.locationKey)
      : null;

    const selectedCountry = isOther
      ? countries.find((c) => c.label === values.country)
      : null;

    const updateData = {
      registrationName: values.registrationName.trim(),
      registrationFurigana: values.registrationFurigana.trim(),
      registrationEmail: values.registrationEmail.trim(),
      registrationPhone: values.registrationPhone.trim(),
      prefecture: trimmedPrefecture,
      country: isJapan ? "日本" : isUS ? "アメリカ" : isOther ? values.country.trim() : "",
      adminArea1: isJapan ? trimmedPrefecture : isUS ? values.usState : "",
      city: isJapan ? trimmedCity : "",
      usState: isUS ? values.usState : "",
      lat: isJapan ? values.lat
        : isUS && selectedState ? selectedState.lat
        : isOther && selectedCountry ? selectedCountry.lat
        : null,
      lng: isJapan ? values.lng
        : isUS && selectedState ? selectedState.lng
        : isOther && selectedCountry ? selectedCountry.lng
        : null,
      locationKey: isJapan
        ? values.locationKey || `JP:${trimmedPrefecture}:${trimmedCity}`
        : isUS
        ? values.locationKey || `US:${values.usState}`
        : isOther && selectedCountry
        ? `COUNTRY:${values.country.trim()}`
        : "",
      geoSource: isJapan ? "japanCities.json" : isUS ? "usStates.json" : isOther ? "countries.js" : "",
      locationLabel: isJapan
        ? `${trimmedPrefecture} ${trimmedCity}`
        : isUS
        ? `アメリカ ${values.usState}`
        : isOther
        ? values.country.trim()
        : trimmedPrefecture,
    };

    await updateDoc(userDocRef, updateData);

    setOpenDialog(false);
    setOpenThankYouDialog(true);
  };

  // console.log("prefecture:", values.prefecture);
  // console.log("cityOptions length:", cityOptions.length);
  // console.log("cityOptions sample:", cityOptions.slice(0, 5));
  return (
    <>
      <Dialog
        open={openDialog}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
        }}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 2,
            bgcolor: theme.surfaceBg,
            border: `1px solid ${theme.bottomBorder}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: theme.railActive,
            fontWeight: 700,
            textAlign: "center",
          }}
        >
          メンバー情報のご登録
        </DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ color: theme.railText, mb: 2 }}>
            ご利用の前に、以下の情報をご入力ください（すべて必須です）。
          </DialogContentText>

          <TextField
            margin="dense"
            required
            label="お名前"
            name="registrationName"
            fullWidth
            value={values.registrationName}
            onChange={handleChange}
            error={Boolean(errors.registrationName)}
            helperText={errors.registrationName}
          />

          <TextField
            margin="dense"
            required
            label="おなまえ（ふりがな）"
            name="registrationFurigana"
            fullWidth
            value={values.registrationFurigana}
            onChange={handleChange}
            error={Boolean(errors.registrationFurigana)}
            helperText={errors.registrationFurigana}
          />

          <TextField
            margin="dense"
            required
            label="メールアドレス"
            name="registrationEmail"
            fullWidth
            value={values.registrationEmail}
            onChange={handleChange}
            error={Boolean(errors.registrationEmail)}
            helperText={errors.registrationEmail}
          />

          <TextField
            margin="dense"
            required
            label="電話番号"
            name="registrationPhone"
            fullWidth
            value={values.registrationPhone}
            onChange={handleChange}
            error={Boolean(errors.registrationPhone)}
            helperText={errors.registrationPhone}
          />

          <FormControl
            fullWidth
            required
            sx={{ mt: 2 }}
            error={Boolean(errors.prefecture)}
          >
            <InputLabel>居住地</InputLabel>
            <Select
              name="prefecture"
              label="居住地"
              value={values.prefecture}
              onChange={handleChange}
            >
              {prefectures.map((pref) => (
                <MenuItem key={pref} value={pref}>
                  {pref}
                </MenuItem>
              ))}
            </Select>
            {errors.prefecture && (
              <Typography
                variant="caption"
                sx={{ color: "#d32f2f", mt: 0.5, ml: 1 }}
              >
                {errors.prefecture}
              </Typography>
            )}
          </FormControl>

          {isJapanesePrefecture && (
            <Box sx={{ mt: 2 }}>
              <Autocomplete
                fullWidth
                options={cityOptions}
                value={selectedCity}
                onChange={(_, newValue) => {
                  if (typeof newValue === "string") {
                    setValues((prev) => ({
                      ...prev,
                      city: newValue,
                      lat: null,
                      lng: null,
                      locationKey: "",
                    }));
                  } else {
                    setValues((prev) => ({
                      ...prev,
                      city: newValue?.name || "",
                      lat: Number.isFinite(newValue?.lat) ? newValue.lat : null,
                      lng: Number.isFinite(newValue?.lng) ? newValue.lng : null,
                      locationKey: newValue?.locationKey || "",
                    }));
                  }

                  setErrors((prev) => ({
                    ...prev,
                    city: "",
                  }));
                }}
                filterOptions={cityFilterOptions}
                autoHighlight
                clearOnBlur={false}
                loading={loadingCities}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option?.name || ""
                }
                isOptionEqualToValue={(option, value) => {
                  const optionName =
                    typeof option === "string" ? option : option?.name || "";
                  const valueName =
                    typeof value === "string" ? value : value?.name || "";
                  return optionName === valueName;
                }}
                noOptionsText={
                  loadingCities
                    ? "読み込み中です…"
                    : "該当する市区町村が見つかりません"
                }
                renderOption={(props, option) => {
                  const label =
                    typeof option === "string" ? option : option?.name || "";

                  return (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        color: "rgba(0,0,0,0.87)",
                        backgroundColor: "#fff",
                        fontSize: 15,
                      }}
                    >
                      {label}
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="市区町村"
                    placeholder="市区町村を入力して検索"
                    error={Boolean(errors.city)}
                    helperText={errors.city}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCities ? (
                            <CircularProgress color="inherit" size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 1,
                  color: "rgba(0,0,0,0.5)",
                }}
              >
                ※市・区・町・村を含む一覧から選択してください。
              </Typography>
            </Box>
          )}

          {isAmerica && (
            <Box sx={{ mt: 2 }}>
              <Autocomplete
                fullWidth
                options={usStateOptions}
                value={selectedUsState}
                onChange={(_, newValue) => {
                  setValues((prev) => ({
                    ...prev,
                    usState: newValue
                      ? newValue.locationKey.replace("US:", "")
                      : "",
                    lat: newValue?.lat ?? null,
                    lng: newValue?.lng ?? null,
                    locationKey: newValue?.locationKey || "",
                  }));
                  setErrors((prev) => ({ ...prev, usState: "" }));
                }}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.locationKey === value?.locationKey
                }
                autoHighlight
                clearOnBlur={false}
                loading={loadingUsStates}
                noOptionsText={
                  loadingUsStates
                    ? "読み込み中です…"
                    : "該当する州が見つかりません"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="州（State）"
                    placeholder="州名を入力して検索"
                    error={Boolean(errors.usState)}
                    helperText={errors.usState}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUsStates ? (
                            <CircularProgress color="inherit" size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 1,
                  color: "rgba(0,0,0,0.5)",
                }}
              >
                ※お住まいの州を選択してください。
              </Typography>
            </Box>
          )}

          {values.prefecture === "海外（その他）" && (
            <>
              <Autocomplete
                fullWidth
                options={countries}
                value={
                  countries.find((c) => c.label === values.country) || null
                }
                onChange={(_, newValue) => {
                  setValues((prev) => ({
                    ...prev,
                    country: newValue ? newValue.label : "",
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    country: "",
                  }));
                }}
                groupBy={(option) => option.region}
                getOptionLabel={(option) =>
                  `${getCountryFlag(option.keywords)} ${option.label}`
                }
                isOptionEqualToValue={(option, value) =>
                  option.label === value.label
                }
                filterOptions={countryFilterOptions}
                autoHighlight
                clearOnBlur={false}
                renderOption={(props, option) => (
                  <li {...props}>
                    <span style={{ marginRight: 8, fontSize: 20 }}>
                      {getCountryFlag(option.keywords)}
                    </span>
                    {option.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    margin="dense"
                    label="国名"
                    placeholder="国名を入力して検索"
                    error={Boolean(errors.country)}
                    helperText={errors.country}
                  />
                )}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <Typography
                      sx={{
                        px: 2,
                        py: 1,
                        fontSize: 12,
                        fontWeight: 700,
                        color: theme.railActive,
                        bgcolor: "rgba(90,53,255,0.06)",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      {params.group}
                    </Typography>
                    <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                  </li>
                )}
                sx={{ mt: 2 }}
              />
              <Typography
                variant="caption"
                sx={{
                  mt: 1,
                  color: "rgba(0,0,0,0.5)",
                }}
              >
                ※国名リストは、現在の国連加盟国（193か国）を基準に表示しています。
              </Typography>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", mt: 1 }}>
          <Button
            variant="contained"
            sx={{
              bgcolor: theme.railActive,
              "&:hover": { bgcolor: theme.railActive },
            }}
            onClick={handleConfirm}
          >
            登録する
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openThankYouDialog}
        onClose={() => setOpenThankYouDialog(false)}
        PaperProps={{
          sx: { borderRadius: 4, p: 2, bgcolor: theme.surfaceBg },
        }}
      >
        <DialogTitle sx={{ color: theme.railActive, textAlign: "center" }}>
          ご登録ありがとうございます
        </DialogTitle>
        <DialogContent>
          <Typography textAlign="center" color={theme.railText}>
            プロフィールが登録されました。
            <br />
            マスターマインドをお楽しみください。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button
            onClick={() => setOpenThankYouDialog(false)}
            sx={{ color: theme.railActive }}
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
